import React, { useEffect, useState } from 'react';

// Shared pieces of the editorial layout used by the case study, Process,
// and Resume pages: a sticky numbered section index and framed image plates.
// Styles live in Work.css under "Case study".

export const pad = (n) => String(n).padStart(2, '0');

// Tracks which section is in view so the sidebar index can highlight it.
// `resetKey` re-subscribes when the page content changes (e.g. a new project).
export function useActiveSection(ids, resetKey) {
  const [active, setActive] = useState(ids[0]);

  useEffect(() => {
    setActive(ids[0]);
    const els = ids.map((id) => document.getElementById(id)).filter(Boolean);
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(e.target.id)),
      { rootMargin: '-35% 0px -60% 0px' }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  return active;
}

// items: [{ id, label, num?, sub? }] — `sub` entries render indented, without a number.
export function SectionIndex({ eyebrow, title, items, active, label = 'Page sections' }) {
  return (
    <nav className="pb-case-index" aria-label={label}>
      {eyebrow && <p className="pb-case-label">{eyebrow}</p>}
      {title && <p className="pb-case-index-title">{title}</p>}
      <span className="pb-case-index-rule" />
      <ol>
        {items.map((s) => (
          <li key={s.id} className={s.sub ? 'sub' : undefined}>
            <a href={`#${s.id}`} className={active === s.id ? 'active' : undefined}>
              {!s.sub && <span>{s.num}</span>}{s.label}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

// A framed image with a two-part caption. Clickable (lightbox) when onOpen is given.
export function Plate({ image, left, right, onOpen }) {
  const frameClass = `pb-plate-frame${image.light ? ' light' : ''}${image.tall ? ' tall' : ''}${image.video ? ' video' : ''}`;
  // Video mockups loop silently like animated images; the lightbox adds controls
  const img = image.video
    ? <video src={image.src} poster={image.poster} aria-label={image.caption} autoPlay muted loop playsInline preload="metadata" />
    : <img src={image.src} alt={image.caption} loading="lazy" />;

  return (
    <figure className="pb-plate">
      {onOpen ? (
        <button type="button" className={frameClass} onClick={onOpen} aria-label={`Enlarge: ${image.caption}`}>
          {img}
        </button>
      ) : (
        <div className={`${frameClass} static`}>{img}</div>
      )}
      {(left || right) && (
        <figcaption>
          <span>{left}</span>
          {right && <span>{right}</span>}
        </figcaption>
      )}
    </figure>
  );
}
